import React, { useState, useEffect, useCallback } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, logInWithEmailAndPassword, registerWithEmailAndPassword } from "../../../../firebase.client.js";
import { sendPasswordResetEmail } from 'firebase/auth';
import Nav from "../Nav";
import Footer from "../Footer";
import Dropdown from 'react-dropdown';
import 'react-dropdown/style.css';
import { useHistory } from "react-router-dom";
import { doc, onSnapshot, collection, query } from "firebase/firestore";
import { db } from "../../../../firebase.client.js";
import {
  getDocs
} from "firebase/firestore";
import './Login.css';

const Login = () => {
  // Authentication & React Router
  const [user, loading] = useAuthState(auth);
  const history = useHistory();

  // UI State
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  // For toggling between "Company" vs "Individual" accounts
  const [userType, setUserType] = useState("Company");
  const [IsIndividual, setIsIndividual] = useState(false); 
    // We'll update this via an effect when userType changes

  // Registration Inputs (controlled)
  const [registerName, setRegisterName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerPasswordRepeat, setRegisterPasswordRepeat] = useState("");
  const [registerStudentName, setRegisterStudentName] = useState("");
  const [registerCompanyCode, setRegisterCompanyCode] = useState("");
  const [registerType, setregisterType] = useState(null); // e.g. { value: 'Student' }
  const [registerTest, setregisterTest] = useState(null);

  // Login Inputs (controlled)
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Terms checkbox
  const [isAccepted, setIsAccepted] = useState(false);

  // For company codes, name usage, etc.
  const [CompanyCodes, setCompanyCodes] = useState([]);
  const [NameId, setNameId] = useState([]);
  const [currName, setCurrName] = useState(''); // used to trigger the name-in-use check
  const [isNameAlreadyInUse, setIsNameAlreadyInUse] = useState(false);
  const [DropdownError, setDropdownError] = useState(false);

  // We pretend you have a flag if it’s a new company (not shown in UI)
  const [IsNewCompany] = useState(false);

  // References to Firestore
  const usersRef = collection(db, "users");

  // Toggle between "login" & "registration"
  const toggleForm = () => {
    setIsRegistering(!isRegistering);
  };

  // Handle userType => "Company" or "Individual"
  useEffect(() => {
    if (userType === 'Individual'){
      setIsIndividual(true);
    } else {
      setIsIndividual(false);
    }
  }, [userType]);

  
  // Fetch existing company codes for validation
  useEffect(() => {
    async function fetchData() {
      try {
        const snap = await getDocs(query(usersRef));
        const codes = [];
        snap.forEach((doc) => {
          if (doc.data().CompanyCode) {
            codes.push(doc.data().CompanyCode);
          }
        });
        setCompanyCodes(codes);
      } catch (err) {
        console.error(err);
      }
    }
    fetchData();
  }, []);

  // Fetch data to see if name is already in use (based on company code, etc.)
  useEffect(() => {
    try {
      const z = query(usersRef);

      getDocs(z)
        .then((querySnapshot) => {
          if (NameId && registerCompanyCode) {
            let newArr = [];

            querySnapshot.docs.forEach((doc) => {
              const data = doc.data();
              if (
                data &&
                data.name &&
                data.uid &&
                data.Type &&
                data.CompanyCode === registerCompanyCode
              ) {
                newArr.push([data.name]);
              }
            });

            let newwerArr = newArr.filter((item) => item[3] !== "Parent");
            setNameId(newwerArr);
          }
        })
        .catch((error) => {
          console.error("Error fetching documents: ", error);
        });
    } catch (err) {
      console.log("err", err);
    }
  }, [registerCompanyCode]);

  // Helper function to see if name is in use
  function IsUserNameInUse(name) {
    return NameId.some((item) => item[0] === name);
  }

  // Re-check whenever user modifies name
  useEffect(() => {
    if (NameId && currName) {
      setIsNameAlreadyInUse(IsUserNameInUse(currName));
    }
  }, [currName, NameId]);

  // Quick message to user if code exists or not
  function FindIfCompanyCodeExists(CompanyCode){
    if(IsIndividual) return null;
    if(CompanyCodes.includes(CompanyCode)){
      return "You are joining an existing company/school.";
    } else if(CompanyCode.trim() !== "") {
      return "You are creating a new company/school account. Make sure you select the correct code.";
    }
    return "";
  }

  function ShowDropdownError(){
    if(DropdownError){
      return(<p className="text-red-600 text-sm mt-2">Error: Missing Data</p>);
    } else {
      return null;
    }
  }

  // --- Registration Validation ---
  // This boolean determines if the "Create Account" button is disabled
  const isRegisterFormValid = (() => {
    if (IsIndividual) {
      // Individual must fill name, email, pass, passRepeat, accept T&C
      return (
        registerName.trim() !== "" &&
        registerEmail.trim() !== "" &&
        registerPassword.trim() !== "" &&
        registerPasswordRepeat.trim() !== "" &&
        registerPassword === registerPasswordRepeat &&
        isAccepted &&
        !isNameAlreadyInUse
      );
    } else {
      // Company user must fill: name, email, pass, passRepeat, accept T&C, pick a type, pick code
      // If Student => also pick test
      if (!registerType || registerCompanyCode.trim() === "") return false;
      if (registerType.value === 'Student' && !registerTest) return false;

      const commonValid =
        registerName.trim() !== "" &&
        registerEmail.trim() !== "" &&
        registerPassword.trim() !== "" &&
        registerPasswordRepeat.trim() !== "" &&
        registerPassword === registerPasswordRepeat &&
        isAccepted &&
        !isNameAlreadyInUse;

      return commonValid;
    }
  })();

  // --- Login Validation ---
  // This boolean determines if the "Log In" button is disabled
  const isLoginFormValid = (
    loginEmail.trim() !== "" &&
    loginPassword.trim() !== "" &&
    !isLoading
  );

  // ---------- Auth Actions ----------

  // 1) Login
  const login = useCallback(async () => {
    if (!isLoginFormValid) return;
    setIsLoading(true);
    try {
      await logInWithEmailAndPassword(loginEmail, loginPassword);
      history.push("/c/dashboard");
      setTimeout(() => window.location.reload(), 50);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [loginEmail, loginPassword, isLoginFormValid, history]);

  // 2) Register
  const register = useCallback(async () => {
    setDropdownError(false);

    // If not valid, show an error & return
    if (!isRegisterFormValid) {
      setDropdownError(true);
      return;
    }

    // Actually create the account
    setIsLoading(true);
    try {
      if (IsIndividual) {
        await registerWithEmailAndPassword(
          registerName,
          registerEmail,
          registerPassword,
          'Individual',
          registerTest?.value || 'Null',
          'none',
          "9999999999",
          'Individual',
          false
        );
      } else if (registerType.value === 'Parent') {
        await registerWithEmailAndPassword(
          registerName,
          registerEmail,
          registerPassword,
          registerType.value,
          registerTest?.value || 'Null',
          registerStudentName,
          "9999999999",
          registerCompanyCode,
          IsNewCompany
        );
      } else {
        // e.g. Student or Tutor
        await registerWithEmailAndPassword(
          registerName,
          registerEmail,
          registerPassword,
          registerType.value,
          registerTest?.value || 'Null',
          '',
          "9999999999",
          registerCompanyCode,
          IsNewCompany
        );
      }
      history.push("/c/dashboard");
      setTimeout(() => window.location.reload(), 50);
    } catch (e) {
      alert(e.message);
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    IsIndividual,
    isRegisterFormValid,
    registerName,
    registerEmail,
    registerPassword,
    registerTest,
    registerType,
    registerStudentName,
    registerCompanyCode,
    IsNewCompany,
    history
  ]);

  // 3) Reset password
  const resetPassword = useCallback(async () => {
    if (!loginEmail) {
      alert("Please enter your email address before requesting a password reset.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, loginEmail);
      alert("Password reset email sent! Check your inbox.");
    } catch (err) {
      alert(err.message);
    }
  }, [loginEmail]);

  // --- UI helpers ---
  const handleCheckboxChange = () => {
    setIsAccepted(!isAccepted);
  };

  const getButtonStyle = (type) => {
    // Toggle between "active" (blue) and normal (gray/white)
    const baseStyle = "mx-2 border px-4 py-2 rounded-md";
    const selectedStyle = "bg-blue-500 text-white border-blue-500";
    const defaultStyle = "border-gray-300 hover:bg-gray-100";
    return `${baseStyle} ${type === userType ? selectedStyle : defaultStyle}`;
  };

  function ShowTestDropdown(forceTest = false){
    try {
      if(forceTest){
        return (
          <div className="flex flex-col mb-4">
            <label htmlFor="registerTest" className="text-sm font-medium text-gray-700 mb-1">Test</label>
            <Dropdown
              options={['SAT','ACT','Diagnostics']}
              onChange={(x)=>{setregisterTest(x)}}
              placeholder="Select a test"
            />
          </div>
        );
      }
      if(IsIndividual){
        return(
          <div className="flex flex-col mb-4">
            <label htmlFor="registerTest" className="text-sm font-medium text-gray-700 mb-1">Test</label>
            <Dropdown
              options={['SAT','ACT','Diagnostics']}
              onChange={(x)=>{setregisterTest(x)}}
              placeholder="Select a test"
            />
          </div>
        );
      }
      if((registerType?.value === 'Tutor') || (registerType?.value === 'Parent')){
        return null;
      }
      return (
        <div className="flex flex-col mb-4">
          <label htmlFor="registerTest" className="text-sm font-medium text-gray-700 mb-1">Test</label>
          <Dropdown
            options={['SAT','ACT','Diagnostics']}
            onChange={(x)=>{setregisterTest(x)}}
            placeholder="Select a test"
          />
        </div>
      );
    } catch(e){
      return null;
    }
  }

  function ShowCompanyCode(){
    if(userType === 'Company'){
      return(
        <div className="flex flex-col mb-4">
          <label htmlFor="registercompanycode" className="text-sm font-medium text-gray-700 mb-1">Company Code</label>
          <input
            type="text"
            name="registercompanycode"
            value={registerCompanyCode}
            onChange={(e) => {
              setRegisterCompanyCode(e.target.value);
            }}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
      );
    }
    return null;
  }

  function ShowStudentName(){
    if(registerType?.value === 'Parent'){
      return(
        <div className="flex flex-col mb-4">
          <label htmlFor="registerstudentname" className="text-sm font-medium text-gray-700 mb-1">Student Name</label>
          <input
            type="text"
            name="registerstudentname"
            value={registerStudentName}
            onChange={(e) => setRegisterStudentName(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
      );
    }
    return null;
  }

  function ShowAccountType(){
    if(!IsIndividual){
      return(
        <div className="flex flex-col mb-4">
          <label htmlFor="registerType" className="text-sm font-medium text-gray-700 mb-1">Account Type</label>
          <Dropdown
            options={['Parent','Student','Tutor']}
            onChange={(s)=>{setregisterType(s)}}
            placeholder="Select an option"
          />
        </div>
      );
    }
    return null;
  }

  function openNewPage() {
    window.open('/terms', '_blank');
  }

  return (
    <>
      <div className="bg-blue-50">
        <Nav />
        <div className="min-h-screen flex flex-col justify-center items-center py-12 px-4">
          <h3 className="text-center text-gray-800 mb-8 text-2xl font-semibold">
            {isRegistering ? "Create an Account" : "Log in to Tutorspace"}
          </h3>

          <div className="w-full max-w-md bg-white rounded-lg shadow-md p-6">
            {isRegistering ? (
              /* ------------------ Registration Form ------------------ */
              <form onSubmit={(e) => { 
                e.preventDefault(); 
                register(); 
              }}>
                {/* User Type Buttons */}
                <div className="flex justify-center mb-6">
                  <button
                    type="button"
                    className={getButtonStyle("Company")}
                    onClick={() => setUserType("Company")}
                  >
                    Company
                  </button>
                  <button
                    type="button"
                    className={getButtonStyle("Individual")}
                    onClick={() => setUserType("Individual")}
                  >
                    Individual
                  </button>
                </div>

                {/* Name */}
                <div className="flex flex-col mb-4">
                  <label htmlFor="registername" className="text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    name="registername"
                    value={registerName}
                    onChange={(e) => {
                      setRegisterName(e.target.value);
                      setCurrName(e.target.value);
                    }}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Email */}
                <div className="flex flex-col mb-4">
                  <label htmlFor="registeremail" className="text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="registeremail"
                    value={registerEmail}
                    onChange={(e) => setRegisterEmail(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {ShowAccountType()}
                {ShowStudentName()}
                {ShowTestDropdown()}
                {ShowCompanyCode()}

                {/* Password */}
                <div className="flex flex-col mb-4">
                  <label htmlFor="registerpassword" className="text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    name="registerpassword"
                    value={registerPassword}
                    onChange={(e) => setRegisterPassword(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Confirm Password */}
                <div className="flex flex-col mb-4">
                  <label htmlFor="registerpasswordrepeat" className="text-sm font-medium text-gray-700 mb-1">Repeat Password</label>
                  <input
                    type="password"
                    name="registerpasswordrepeat"
                    value={registerPasswordRepeat}
                    onChange={(e) => setRegisterPasswordRepeat(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Company Code message (if any) */}
                <p className="text-center text-sm text-gray-600 my-2">
                  {FindIfCompanyCodeExists(registerCompanyCode)}
                </p>

                {/* Name in use message (if any) */}
                {isNameAlreadyInUse && (
                  <p className="text-center text-sm text-red-600 my-2">
                    Name already in use. Please choose another or add a character/number.
                  </p>
                )}

                {ShowDropdownError()}

                {/* Terms & Conditions */}
                <div className="flex items-center mb-6">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={isAccepted}
                    onChange={handleCheckboxChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label htmlFor="acceptTerms" className="ml-2 text-sm text-gray-700">
                    I accept the{" "}
                    <button
                      type="button"
                      className="underline text-blue-500 hover:text-blue-700"
                      onClick={openNewPage}
                    >
                      Terms and Conditions
                    </button>
                  </label>
                </div>

                {/* Create Account Button (Disabled if form not valid) */}
                <button
                  type="submit"
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 rounded focus:outline-none"
                  disabled={!isRegisterFormValid || isLoading}
                >
                  {isLoading ? 'Creating account...' : 'Create Account'}
                </button>
              </form>
            ) : (
              /* ------------------ Login Form ------------------ */
              <form onSubmit={(e) => {
                e.preventDefault();
                login();
              }}>
                {/* Email */}
                <div className="flex flex-col mb-4">
                  <label htmlFor="loginemail" className="text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="loginemail"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Password + Show/Hide */}
                <div className="flex flex-col mb-2">
                  <label htmlFor="loginpassword" className="text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <input
                      type={isPasswordVisible ? "text" : "password"}
                      name="loginpassword"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                      className="absolute right-2 top-2 text-sm text-blue-600 hover:underline focus:outline-none"
                    >
                      {isPasswordVisible ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                {/* Forgot Password */}
                <div className="flex justify-end mb-6">
                  <button
                    type="button"
                    className="text-sm text-blue-500 hover:underline"
                    onClick={resetPassword}
                  >
                    Forgot Password?
                  </button>
                </div>

                {/* Login Button (Disabled if form not valid) */}
                <button
                  type="submit"
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 rounded focus:outline-none"
                  disabled={!isLoginFormValid}
                >
                  {isLoading ? 'Logging in...' : 'Log In'}
                </button>
              </form>
            )}

            {/* Toggle Form */}
            <div className="mt-6 flex justify-center">
              <button
                className="text-blue-600 hover:underline text-sm font-medium"
                onClick={toggleForm}
              >
                {isRegistering
                  ? "Already have an account? Log In"
                  : "Don't have an account? Create One"
                }
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default Login;
